import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, BookOpenIcon } from "@heroicons/react/24/outline";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24 border-b border-slate-100 py-8 last:border-b-0">
    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>
  </section>
);

const GuideShot = ({ src, alt }) => (
  <figure className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
    <img src={src} alt={alt} className="w-full" />
    <figcaption className="px-3 py-2 text-xs text-slate-500">{alt}</figcaption>
  </figure>
);

export const UserGuideDrawingsGettingStarted = () => {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    document.title = `${t("user_guide.drawings.title")} | Promang`;
  }, [t]);

  useEffect(() => {
    const raw = location.hash?.replace(/^#/, "");
    if (!raw) return;
    const el = document.getElementById(raw);
    if (el) {
      window.requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <Link
          to="/dashboard/guide"
          className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t("user_guide.drawings.back_to_hub")}
        </Link>

        <div className="rounded-[2rem] border border-slate-200 bg-white px-6 py-8 shadow-sm sm:px-8">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
            <BookOpenIcon className="h-4 w-4" />
            {t("user_guide.drawings.badge")}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
            {t("user_guide.drawings.title")}
          </h1>
          <p className="mt-3 text-sm text-slate-600">{t("user_guide.drawings.purpose")}</p>
          <p className="mt-2">
            <Link
              to="/dashboard/drawings?select_project=1"
              className="text-sm font-semibold text-[#1F4BE0] underline decoration-slate-300"
            >
              {t("user_guide.drawings.open_module")}
            </Link>
          </p>

          <Section id="project" title={t("user_guide.drawings.section.project.title")}>
            <ol className="list-inside list-decimal space-y-2">
              <li>{t("user_guide.drawings.section.project.li1")}</li>
              <li>{t("user_guide.drawings.section.project.li2")}</li>
              <li>{t("user_guide.drawings.section.project.li3")}</li>
            </ol>
            <GuideShot
              src="/user-guide/drawings/01-velg-prosjekt.png"
              alt={t("user_guide.drawings.section.project.shot")}
            />
          </Section>

          <Section id="list" title={t("user_guide.drawings.section.list.title")}>
            <p>{t("user_guide.drawings.section.list.p1")}</p>
            <ul className="list-inside list-disc space-y-2">
              <li>{t("user_guide.drawings.section.list.li1")}</li>
              <li>{t("user_guide.drawings.section.list.li2")}</li>
              <li>{t("user_guide.drawings.section.list.li3")}</li>
            </ul>
            <GuideShot
              src="/user-guide/drawings/02-tegningsliste.png"
              alt={t("user_guide.drawings.section.list.shot")}
            />
          </Section>

          <Section id="house" title={t("user_guide.drawings.section.house.title")}>
            <ol className="list-inside list-decimal space-y-2">
              <li>{t("user_guide.drawings.section.house.li1")}</li>
              <li>{t("user_guide.drawings.section.house.li2")}</li>
              <li>{t("user_guide.drawings.section.house.li3")}</li>
            </ol>
            <GuideShot
              src="/user-guide/drawings/03-legg-til-hus.png"
              alt={t("user_guide.drawings.section.house.shot")}
            />
          </Section>

          <Section id="floor" title={t("user_guide.drawings.section.floor.title")}>
            <ol className="list-inside list-decimal space-y-2">
              <li>{t("user_guide.drawings.section.floor.li1")}</li>
              <li>{t("user_guide.drawings.section.floor.li2")}</li>
              <li>{t("user_guide.drawings.section.floor.li3")}</li>
              <li>{t("user_guide.drawings.section.floor.li4")}</li>
              <li>{t("user_guide.drawings.section.floor.li5")}</li>
            </ol>
            <GuideShot
              src="/user-guide/drawings/04-legg-til-etasje.png"
              alt={t("user_guide.drawings.section.floor.shot")}
            />
            <p>{t("user_guide.drawings.section.floor.p1")}</p>
            <GuideShot
              src="/user-guide/drawings/05-struktur-hus-etasjer.png"
              alt={t("user_guide.drawings.section.floor.shot_tree")}
            />
          </Section>

          <Section id="upload" title={t("user_guide.drawings.section.upload.title")}>
            <ol className="list-inside list-decimal space-y-2">
              <li>{t("user_guide.drawings.section.upload.li1")}</li>
              <li>{t("user_guide.drawings.section.upload.li2")}</li>
              <li>{t("user_guide.drawings.section.upload.li3")}</li>
              <li>{t("user_guide.drawings.section.upload.li4")}</li>
              <li>{t("user_guide.drawings.section.upload.li5")}</li>
              <li>{t("user_guide.drawings.section.upload.li6")}</li>
              <li>{t("user_guide.drawings.section.upload.li7")}</li>
              <li>{t("user_guide.drawings.section.upload.li8")}</li>
            </ol>
            <GuideShot
              src="/user-guide/drawings/06-ny-tegning-skuff.png"
              alt={t("user_guide.drawings.section.upload.shot_empty")}
            />
            <GuideShot
              src="/user-guide/drawings/07-last-opp-pdf-utfylt.png"
              alt={t("user_guide.drawings.section.upload.shot_filled")}
            />
          </Section>

          <Section id="done" title={t("user_guide.drawings.section.done.title")}>
            <p>{t("user_guide.drawings.section.done.p1")}</p>
            <GuideShot
              src="/user-guide/drawings/09-tegningsliste-ferdig.png"
              alt={t("user_guide.drawings.section.done.shot")}
            />
          </Section>

          <Section id="rules" title={t("user_guide.drawings.section.rules.title")}>
            <ul className="list-inside list-disc space-y-2">
              <li>{t("user_guide.drawings.section.rules.li1")}</li>
              <li>{t("user_guide.drawings.section.rules.li2")}</li>
              <li>{t("user_guide.drawings.section.rules.li3")}</li>
              <li>{t("user_guide.drawings.section.rules.li4")}</li>
            </ul>
          </Section>
        </div>
      </div>
    </div>
  );
};
